import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Mail, Trash2, Copy } from "lucide-react";
import { ASSIGNABLE_ROLES, ROLE_LABELS } from "@/lib/permissions";

const emptyInvite = { name: "", email: "", role: "intake_staff" };
const emptyCreate = { name: "", email: "", password: "", role: "intake_staff" };

export default function Team() {
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [invite, setInvite] = useState(emptyInvite);
  const [create, setCreate] = useState(emptyCreate);
  const [inviteLink, setInviteLink] = useState("");
  const [seats, setSeats] = useState(null);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api.get("/team/seats").then((r) => setSeats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const full = seats && seats.limit !== null && (seats.used + seats.pending) >= seats.limit;

  const sendInvite = async () => {
    if (!invite.email || !invite.name) return toast.error("Name and email are required");
    try {
      const { data } = await api.post("/users/invite", invite);
      setInviteLink(data.invite_link);
      toast.success(data.email_sent ? "Invitation email sent" : "Invite created (email not sent — copy the link below)");
      setInvite(emptyInvite);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const createUser = async () => {
    if (!create.email || !create.name || !create.password) return toast.error("All fields are required");
    try {
      await api.post("/users", create);
      toast.success("Team member added");
      setCreate(emptyCreate);
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeUser = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(inviteLink);
    toast.success("Invite link copied");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Access control</p>
          <h1 className="font-display text-4xl font-light mt-1">Team &amp; Roles</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Invite teammates or create accounts, and control what each role can do.
          </p>
          {seats && (
            <div className="mt-3 flex items-center gap-2" data-testid="seat-usage">
              <span className="font-mono-data text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-700 px-2 py-1">
                {seats.used + seats.pending} of {seats.limit === null ? "∞" : seats.limit} seats used
              </span>
              {full && (
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#B54747]">
                  Limit reached — upgrade to add more
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) setInviteLink(""); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none border-zinc-300" disabled={full} data-testid="invite-user-button">
                <Mail className="h-4 w-4 mr-1" /> Invite by email
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Invite a teammate</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Name</Label>
                  <Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name-input" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email-input" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {inviteLink && (
                  <div className="border border-zinc-200 bg-zinc-50 p-3">
                    <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Invite link</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs break-all flex-1" data-testid="invite-link">{inviteLink}</code>
                      <button onClick={copyLink} className="text-zinc-500 hover:text-black shrink-0" data-testid="copy-invite-link"><Copy className="h-4 w-4" /></button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={sendInvite} className="bg-[#0B1F33] text-white rounded-none" data-testid="send-invite-button">Send invitation</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0B1F33] text-white rounded-none hover:bg-zinc-800" disabled={full} data-testid="create-user-button">
                <UserPlus className="h-4 w-4 mr-1" /> Add member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Add a team member</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Name</Label>
                  <Input value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} data-testid="create-name-input" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={create.email} onChange={(e) => setCreate({ ...create, email: e.target.value })} data-testid="create-email-input" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Temporary password</Label>
                  <Input type="text" value={create.password} onChange={(e) => setCreate({ ...create, password: e.target.value })} data-testid="create-password-input" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={create.role} onValueChange={(v) => setCreate({ ...create, role: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="create-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createUser} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-user-button">Create member</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name} {u.is_self && <span className="text-zinc-400 text-xs">(you)</span>}</TableCell>
                <TableCell className="font-mono-data text-sm text-zinc-600">{u.email}</TableCell>
                <TableCell>
                  {u.is_self ? (
                    <span className="text-sm">{u.role_label}</span>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="w-48 rounded-none h-9" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ASSIGNABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {!u.is_self && (
                    <button onClick={() => removeUser(u.id)} className="text-zinc-300 hover:text-[#B54747]" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-zinc-500 py-10">No team members yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
